import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useDeleteUser } from 'app/hooks/useDeleteUser'
import { legacyRoleRank } from 'app/types/roles'
import { UserJson } from 'app/types/entities'
import { RoleJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { useUserStore, useRoleStore } from 'app/stores/StoreContext'
import { gqlFetchList } from 'app/lib/graphqlClient'

export type UseUserWorkspaceReturn = {
  me: ReturnType<typeof useUser>
  user: UserJson
  userId: string | undefined
  currentWorkspace: ReturnType<typeof useWorkspace>
  isViewingSelf: boolean
  canEdit: boolean
  canRemove: boolean
  workspaceRoles: RoleJson[]
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  role: number
  setRole: (r: number) => void
  roleId: string
  setRoleId: (r: string) => void
  selectedStaff: OptionType | undefined
  setSelectedStaff: (s: OptionType | undefined) => void
  locationInputs: Array<{ name: string; value: string }>
  setLocationInputs: (inputs: Array<{ name: string; value: string }>) => void
  isMultiWorkspace: boolean
  isDeleting: boolean
  removeUserHandler: () => Promise<void>
  onSave: (body: Record<string, unknown>) => void
}

export function useUserWorkspace(): UseUserWorkspaceReturn {
  const me = useUser()
  const navigate = useNavigate()

  const { userId } = useParams()
  const currentWorkspace = useWorkspace()
  const userStore = useUserStore()
  const roleStore = useRoleStore()

  const checkPermission = useCheckPermission()
  const canManageUsers = checkPermission([`org:settings:manage-users`, `users:manage`])

  const [user, setUser] = useState<UserJson>({} as UserJson)
  const [workspaceRoles, setWorkspaceRoles] = useState<RoleJson[]>([])
  const { deleteUser, isDeleting } = useDeleteUser()

  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [role, setRole] = useState<number>(legacyRoleRank.workspaceUser)
  const [roleId, setRoleId] = useState<string>(``)
  const [selectedStaff, setSelectedStaff] = useState<OptionType | undefined>()
  const [locationInputs, setLocationInputs] = useState<Array<{ name: string; value: string }>>([])

  useEffect(() => {
    if (!userId || userId === `new`) return
    userStore
      .fetchOne(userId)
      .then((result) => {
        const u = (result.data as { user?: UserJson } | undefined)?.user
        if (!u) return
        setUser(u)
        setRole(u.role ?? legacyRoleRank.workspaceUser)
        setRoleId(u.roleId ?? ``)
      })
      .catch((err) => logError(`Error fetching user workspace data`, err))
  }, [userId])

  useEffect(() => {
    roleStore
      .fetchList({ pagination: { page: 0, limit: 100 } })
      .then((result) => {
        const roles = (result.data as { roles?: { hits?: RoleJson[] } } | undefined)?.roles?.hits ?? []
        if (roles.length > 0) setWorkspaceRoles(roles)
      })
      .catch((err) => logError(`Error fetching workspace roles`, err))
  }, [])

  useEffect(() => {
    if (!user.id) return
    bg(async () => {
      const result = await gqlFetchList<{ id: string; name: string; userId: string }>(
        'users',
        { search: `type:staff AND userId:${user.id}` },
        'id name userId',
      )
      const staff = result.hits[0]
      setSelectedStaff(staff ? { id: staff.id!, name: staff.name ?? ``, value: staff.id! } : undefined)
    })
  }, [user.id])

  // Permission rules mirror the loader that used to compute them server-side:
  // you can always edit yourself, super users and user managers can edit anyone,
  // and removing a user is for managers acting on someone other than themselves.
  const isSuperUser = me?.type === `super`
  const isViewingSelf = !!me?.id && !!user.id && me.id === user.id
  const canEdit = !!isSuperUser || isViewingSelf || canManageUsers
  const canRemove = (!!isSuperUser || canManageUsers) && !isViewingSelf
  const isMultiWorkspace = (user.workspaces?.length ?? 0) > 1

  const onSave = (body: Record<string, unknown>) => {
    const allowedLocationIds = locationInputs
      .filter((input) => input.name === `allowedLocationIds` && input.value)
      .map((input) => input.value)
    const input = {
      name: body.name as string,
      email: body.email as string,
      phone: body.phone as string,
      roleId: (body.roleId as string) || undefined,
      allowedLocationIds,
    }
    userStore.update(userId!, input).then((result) => {
      if (!result.error) {
        notifySuccess(`Workspace settings saved`)
        setIsDirtyForm(false)
        navigate(`/settings/users`)
      } else {
        notifyError(result.error.message)
      }
    })
  }

  const removeUserHandler = () =>
    deleteUser(userId!, {
      confirmDescription: `Are you sure you want to delete ${user.name}? This action cannot be undone.`,
    })

  return {
    me,
    user,
    userId,
    currentWorkspace,
    isViewingSelf,
    canEdit,
    canRemove,
    workspaceRoles,
    isDirtyForm,
    setIsDirtyForm,
    role,
    setRole,
    roleId,
    setRoleId,
    selectedStaff,
    setSelectedStaff,
    locationInputs,
    setLocationInputs,
    isMultiWorkspace,
    isDeleting,
    removeUserHandler,
    onSave,
  }
}
